import { FormikProps } from "formik";
import CrossIcon from "../../../../icons/cross.icon";
import PlusIcon from "../../../../icons/plus.icon";
import DropdownSelector from "../../../../Common/dropdown-selector";
import cn from "../../../../../Utils/class-names.utils";
import styles from "./index.module.scss";
import { IProductFormValues } from "../../../../../common/validations/product.validations";
import FormRow from "../form-row";

interface ICategoryItem {
  label: string;
  value: string;
  image?: string;
}

interface ITool {
  id: number;
  name: string;
  image: string;
}

interface ISupplier {
  id: number;
  name: string;
}


interface ProductDetailsProps extends FormikProps<IProductFormValues> {}

const ProductDetails = ({
  values,
  setFieldValue,
}: ProductDetailsProps) => {
  const categoryOptions: ICategoryItem[] = [
    { label: "Electronics", value: "electronics" },
    { label: "Clothing", value: "clothing" },
    { label: "Home & Garden", value: "home" },
    { label: "Sports & Outdoors", value: "sports" },
    { label: "Books", value: "books" },
  ];

  const availableTools: ICategoryItem[] = [
    { label: "Coro", value: "coro", image: '/images/attack-surface/generic2.svg' },
    { label: "Microsoft Defender", value: "microsoft-defender", image: '/images/attack-surface/generic2.svg' },
    { label: "CrowdStrike", value: "crowdstrike", image: '/images/attack-surface/generic2.svg' },
    { label: "SentinelOne", value: "sentinelone", image: '/images/attack-surface/generic2.svg' },
    { label: "Sophos", value: "sophos", image: '/images/attack-surface/generic2.svg' },
    { label: "Kaspersky", value: "kaspersky", image: '/images/attack-surface/generic2.svg' },
  ];

  // Mock data for suppliers
  const availableSuppliers: ICategoryItem[] = [
    { label: "M3 Networks", value: "m3-networks" },
    { label: "TechCorp Solutions", value: "techcorp-solutions" },
    { label: "Global IT Partners", value: "global-it-partners" },
    { label: "SecureNet Systems", value: "securenetsystems" },
    { label: "CloudTech Industries", value: "cloudtech-industries" },
    { label: "DataSecure Inc", value: "datasecure-inc" },
  ];

  // Mock data for manufacturers
  const availableManufacturers: ICategoryItem[] = [
    { label: "M3 Networks", value: "m3-networks" },
    { label: "Dell Technologies", value: "dell-technologies" },
    { label: "HP Inc", value: "hp-inc" },
    { label: "Lenovo", value: "lenovo" },
    { label: "Cisco Systems", value: "cisco-systems" },
    { label: "IBM Corporation", value: "ibm-corporation" },
  ];

  const selectedCategory = values.category;
  const subcategory = values.subcategory;
  const linkedTools = values.linkedTools;
  const selectedSuppliers = values.suppliers;
  const selectedManufacturers = values.manufacturers;

  const handleRemoveSupplier = (id: number) => {
    setFieldValue(
      'suppliers',
      selectedSuppliers.filter((supplier) => supplier.id !== id),
    );
  };

  const handleRemoveManufacturer = (id: number) => {
    setFieldValue(
      'manufacturers',
      selectedManufacturers.filter((manufacturer) => manufacturer.id !== id),
    );
  };

  const handleAddSupplier = (item: ICategoryItem) => {
    const newSupplier: ISupplier = {
      id: Date.now(),
      name: item.label,
    };
    setFieldValue('suppliers', [...selectedSuppliers, newSupplier]);
  };

  const handleAddManufacturer = (item: ICategoryItem) => {
    const newManufacturer: ISupplier = {
      id: Date.now(),
      name: item.label,
    };
    setFieldValue('manufacturers', [...selectedManufacturers, newManufacturer]);
  };

  const handleRemoveTool = (id: number) => {
    setFieldValue(
      'linkedTools',
      linkedTools.filter((tool) => tool.id !== id),
    );
  };

  const handleAddTool = (item: ICategoryItem) => {
    const newTool: ITool = {
      id: Date.now(),
      name: item.label,
      image: '/images/attack-surface/generic2.svg',
    };
    setFieldValue('linkedTools', [...linkedTools, newTool]);
  };

  // Filter out already selected items from dropdown options
  const getAvailableTools = () => {
    const selectedToolValues = linkedTools.map(tool => {
      const found = availableTools.find(opt => opt.label === tool.name);
      return found?.value;
    }).filter(Boolean);
    return availableTools.filter(tool => !selectedToolValues.includes(tool.value));
  };

  const getAvailableSuppliers = () => {
    const selectedSupplierNames = selectedSuppliers.map(s => s.name);
    return availableSuppliers.filter(supplier => !selectedSupplierNames.includes(supplier.label));
  };

  const getAvailableManufacturers = () => {
    const selectedManufacturerNames = selectedManufacturers.map(m => m.name);
    return availableManufacturers.filter(manufacturer => !selectedManufacturerNames.includes(manufacturer.label));
  };

  const handleCategoryChange = (item: ICategoryItem) => {
    setFieldValue('category', item);
  };

  const handleSubcategoryChange = (item: ICategoryItem) => {
    setFieldValue('subcategory', item);
  };

  return (
    <div className="d-flex flex-column gap-16">
      <FormRow label="Product Name" htmlFor="product-name-input" styles={styles}>
        <div className={styles.inputContainer}>
          <div className={styles.inputWrapper}>
            <input
              id="product-name-input"
              type="text"
              className={styles.input}
              placeholder=""
              aria-label="Product Name"
              value={values.productName}
              onChange={(e) => setFieldValue('productName', e.target.value)}
              onBlur={() => setFieldValue('productName', values.productName)}
            />
          </div>
        </div>
      </FormRow>

      <FormRow label="Product SKU" htmlFor="product-sku-input" styles={styles}>
        <div className={styles.inputContainer}>
          <div className={styles.inputWrapper}>
            <input
              id="product-sku-input"
              type="text"
              className={styles.input}
              placeholder=""
              aria-label="Product SKU"
              value={values.productSku}
              onChange={(e) => setFieldValue('productSku', e.target.value)}
              onBlur={() => setFieldValue('productSku', values.productSku)}
            />
          </div>
        </div>
      </FormRow>

      <FormRow label="Linked Tools" htmlFor="linked-tools" styles={styles}>
        <div
          id="linked-tools"
          className={styles.toolsContainer}
          role="list"
          aria-label="Linked tools"
        >
          {linkedTools.map((tool) => (
            <div
              key={tool.id}
              className={styles.toolChip}
              role="listitem"
            >
              <div className={styles.toolIconWrapper}>
                <img
                  className={styles.toolIcon}
                  alt={`${tool.name} icon`}
                  src={tool.image}
                />
              </div>

              <div className={styles.toolNameWrapper}>
                <div className={styles.toolName}>
                  {tool.name}
                </div>
              </div>

              <div className={styles.toolIconWrapper}>
                <button
                  type="button"
                  onClick={() => handleRemoveTool(tool.id)}
                  className={cn(styles.removeButton, "my-button")}
                  aria-label={`Remove ${tool.name}`}
                >
                  <div className={styles.removeButtonInner}>
                    <CrossIcon />
                  </div>
                </button>
              </div>
            </div>
          ))}

          <DropdownSelector
            list={getAvailableTools()}
            selected={null}
            onItemSelected={handleAddTool}
            toggleClassName={styles.addSupplierButton}
            className={styles.addDropdownWrapper}
            showSelectedIcon={false}
          >
            <div className={styles.addSupplierIconWrapper}>
              <PlusIcon />
            </div>

            <div className={styles.addSupplierTextWrapper}>
              <span className={styles.addSupplierText}>
                Add
              </span>
            </div>
          </DropdownSelector>
        </div>
      </FormRow>

      <FormRow label="Category" htmlFor="category-select" styles={styles}>
        <div className={styles.inputContainer}>
          <DropdownSelector
            list={categoryOptions}
            selected={selectedCategory}
            onItemSelected={handleCategoryChange}
            toggleClassName={styles.selectToggle}
            className={styles.dropdownWrapper}
          >
            <div className={styles.inputWrapper}>
              <div className={styles.selectToggleContent}>
                <span className={styles.selectText}>
                  {selectedCategory ? selectedCategory.label : "Choose"}
                </span>
                <div className={styles.selectIconWrapper}>
                  <div className={styles.selectIcon}>
                    <div className="arrow-icon" />
                  </div>
                </div>
              </div>
            </div>
          </DropdownSelector>
        </div>
      </FormRow>

      <FormRow label="Subcategory" htmlFor="subcategory-select" styles={styles}>
        <div className={styles.inputContainer}>
          <DropdownSelector
            list={categoryOptions}
            selected={subcategory}
            onItemSelected={handleSubcategoryChange}
            toggleClassName={styles.selectToggle}
            className={styles.dropdownWrapper}
          >
            <div className={styles.inputWrapper}>
              <div className={styles.selectToggleContent}>
                <span className={styles.selectText}>
                  {subcategory ? subcategory.label : "Choose"}
                </span>
                <div className={styles.selectIconWrapper}>
                  <div className={styles.selectIcon}>
                    <div className="arrow-icon" />
                  </div>
                </div>
              </div>
            </div>
          </DropdownSelector>
        </div>
      </FormRow>

      <FormRow label="Supplier" variant="supplier" styles={styles}>
        <div className={styles.suppliersContainer}>
          {selectedSuppliers.map((supplier) => (
            <div
              key={supplier.id}
              className={styles.supplierChip}
            >
              <div className={styles.supplierIconWrapper}>
                {/* <BaseCircleM className="!relative !w-5 !h-5" /> */}
              </div>

              <div className={styles.supplierNameWrapper}>
                <span className={styles.supplierName}>
                  {supplier.name}
                </span>
              </div>

              <div className={styles.supplierRemoveButtonWrapper}>
                <button
                  type="button"
                  onClick={() => handleRemoveSupplier(supplier.id)}
                  className={cn(styles.removeButton, "my-button")}
                  aria-label={`Remove ${supplier.name}`}
                >
                  <div className={styles.removeButtonInner}>
                    <CrossIcon />
                  </div>
                </button>
              </div>
            </div>
          ))}

          <DropdownSelector
            list={getAvailableSuppliers()}
            selected={null}
            onItemSelected={handleAddSupplier}
            toggleClassName={styles.addSupplierButton}
            className={styles.addDropdownWrapper}
            showSelectedIcon={false}
          >
            <div className={styles.addSupplierIconWrapper}>
              <PlusIcon />
            </div>

            <div className={styles.addSupplierTextWrapper}>
              <span className={styles.addSupplierText}>
                Add
              </span>
            </div>
          </DropdownSelector>
        </div>
      </FormRow>

      <FormRow label="Manufacturer" variant="supplier" styles={styles}>
        <div className={styles.suppliersContainer}>
          {selectedManufacturers.map((manufacturer) => (
            <div
              key={manufacturer.id}
              className={styles.supplierChip}
            >
              <div className={styles.supplierIconWrapper}>
                {/* <BaseCircleM className="!relative !w-5 !h-5" /> */}
              </div>

              <div className={styles.supplierNameWrapper}>
                <span className={styles.supplierName}>
                  {manufacturer.name}
                </span>
              </div>

              <div className={styles.supplierRemoveButtonWrapper}>
                <button
                  type="button"
                  onClick={() => handleRemoveManufacturer(manufacturer.id)}
                  className={cn(styles.removeButton, "my-button")}
                  aria-label={`Remove ${manufacturer.name}`}
                >
                  <div className={styles.removeButtonInner}>
                    <CrossIcon />
                  </div>
                </button>
              </div>
            </div>
          ))}

          <DropdownSelector
            list={getAvailableManufacturers()}
            selected={null}
            onItemSelected={handleAddManufacturer}
            toggleClassName={styles.addSupplierButton}
            className={styles.addDropdownWrapper}
            showSelectedIcon={false}
          >
            <div className={styles.addSupplierIconWrapper}>
              <PlusIcon />
            </div>

            <div className={styles.addSupplierTextWrapper}>
              <span className={styles.addSupplierText}>
                Add
              </span>
            </div>
          </DropdownSelector>
        </div>
      </FormRow>
    </div>
  );
};

export default ProductDetails;